import { createTransport, HttpAuthTransportSymbol, HttpTransportRequestCancel } from '@goodt-common/net';
import { ServiceProvider } from '@goodt-common/service-provider';
import { DatasetClientSymbol } from './service-provider-config';
import { DataClientRequestError } from './errors';

/** @type {import('./client').DataClientConfig} */
const ConfigDefaults = {
    isAdmin: false,
    processResponseError: null,
    processResponseResult: null
};

/**
 * @param {import('@goodt-wcore/net').ITransportOptions} transportOptions
 * @return {Record.<string, import('./client').IRequestAction>}
 */
const DataClientApiActions = (transportOptions = {}) => ({
    DATA: {
        url: 'api/dataset/data',
        options: {
            method: 'post',
            ...transportOptions
        }
    }
});

/**
 *
 * @param {{ action:import('./client').IRequestAction, baseURL:string, params:object, pathParams:object }} options
 * @return {import('@goodt-wcore/net').ITransportRequest}
 */
const buildTransportRequest = ({ action, baseURL = '', params = {}, pathParams = {} }) => {
    const url = Object.entries(pathParams).reduce(
        (finalUrl, [key, value]) => finalUrl.replace(new RegExp(`:${key}`, 'g'), String(value)),
        action.url
    );

    return {
        url,
        method: action.options.method,
        params,
        options: {
            baseURL,
            ...action.options
        }
    };
};

export class DataClient {
    /**
     * @private
     * @type {import('@goodt-common/net').ITransport}
     */
    _transport;

    /** @type {import('./client').DataClientConfig} */
    config;

    /**
     * Constructor
     * @param {import('./client').DataClientConfig} [config] config
     */
    constructor(config = {}) {
        this.config = { ...ConfigDefaults, ...config };
        const { isAdmin } = this.config;
        // eslint-disable-next-line ban/ban
        this._transport = createTransport(
            HttpAuthTransportSymbol,
            { headers: { 'x-dremio-admin': isAdmin } },
            { globalError: false } // disable http transport global error
        );
    }

    /**
     * @param {import('./types').IDatasetRequest} datasetRequest
     * @param {import('./client').IDatasetRequestOptions} options
     * @return {Promise<import('./types').IDatasetResult>}
     */
    async getData({ query, url: baseURL, limit = 10 }, { offset = 0, debug = false } = {}) {
        const params = {
            query,
            limit,
            offset,
            debug: debug ?? undefined
        };
        /** @type {import('./types').IDatasetResult} */
        const result = await this.requestAction({ action: DataClientApiActions().DATA, params, baseURL });
        
        return result;
    }

    /**
     * @return {import('@goodt-common/net').TransportRequestCancel}
     */
    getNextRequestCancel() {
        return this._transport.getNextRequestCancel();
    }

    cancelActiveRequests() {
        this._transport.dispose();
    }

    /**
     * @param {{ action: import('./client').IRequestAction, baseURL: string, params: object, pathParams: object }} options
     * @throws {DataClientRequestError|HttpTransportRequestCancel|Error}
     * @return {Promise}
     */
    requestAction({ action, baseURL = '', params = {}, pathParams = {}, options = {} }) {
        const { processResponseError, processResponseResult } = this.config;

        return this._transport
            .request(buildTransportRequest({ action, baseURL, params, pathParams, options }))
            .then((result) => (processResponseResult != null ? processResponseResult(result) : result))
            .catch((error) => {
                if (processResponseError != null) {
                    // should throw error
                    processResponseError(error);
                }
                if (error instanceof HttpTransportRequestCancel) {
                    throw error;
                }
                throw new DataClientRequestError(error.message, { reason: error });
            });
    }
}

export const createDatasetClient = (config) =>
    new DataClient({
        /* processResponseError: handleError, */
        processResponseResult: ({ data: { rows, ...rest } }) => ({ ...rest, rows: rows.map(Object.freeze) }),
        ...config
    });

export const DatasetClientFactory = (config = {}) => ServiceProvider.use(DatasetClientSymbol).withOptions(config).get();
